import { useCallback } from "react"
import { download, generateCsv, mkConfig } from "export-to-csv"

import { toCsvText } from "../../../../../lib/stringLib"
import { isMultiDay } from "../../../../../lib/time"
import { toDineroOrUndefined } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"

import { getLocationText } from "../upcomingMeetingsLib"
import type { UpcomingMeetingRow } from "../upcomingMeetingsTypes"

export function useExportUpcomingMeetings(rows: ReadonlyArray<UpcomingMeetingRow>) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})
    const { l10n } = useAppLocalization()

    return useCallback(() => {
        const dateHeader = l10n.getString("service-center-reports-upcoming-meetings-csv-date-header")
        const numberHeader = l10n.getString("service-center-reports-upcoming-meetings-csv-number-header")
        const startHeader = l10n.getString("service-center-reports-upcoming-meetings-csv-start-header")
        const endHeader = l10n.getString("service-center-reports-upcoming-meetings-csv-end-header")
        const titleHeader = l10n.getString("service-center-reports-upcoming-meetings-csv-title-header")
        const typeHeader = l10n.getString("service-center-reports-upcoming-meetings-csv-type-header")
        const locationHeader = l10n.getString("service-center-reports-upcoming-meetings-csv-location-header")
        const priceHeader = l10n.getString("service-center-reports-upcoming-meetings-csv-price-header")

        const csvOptions = mkConfig({
            fieldSeparator: ";",
            quoteStrings: true,
            useBom: true,
            columnHeaders: [
                dateHeader,
                numberHeader,
                startHeader,
                endHeader,
                titleHeader,
                typeHeader,
                locationHeader,
                priceHeader,
            ],
            filename: `Facilitynet-upcoming-meetings-${dateTimeFactory.formatDate(dateTimeFactory.now())}`,
        })

        const exportRows = rows.map((row) => {
            const start = dateTimeFactory.fromApiOrUndefined(
                row.meetingDetails.startDateTime || row.deliveryDetails.time,
            )
            const end = dateTimeFactory.fromApiOrUndefined(row.meetingDetails.endDateTime)
            const price = toDineroOrUndefined(row.amount)

            return {
                [dateHeader]: start ? dateTimeFactory.formatDate(start) : "",
                [numberHeader]: row.id,
                [startHeader]: start ? dateTimeFactory.formatTime(start) : "",
                [endHeader]: end
                    ? isMultiDay(start, end)
                        ? dateTimeFactory.formatDateTime(end)
                        : dateTimeFactory.formatTime(end)
                    : "",
                [titleHeader]: toCsvText(row.meetingDetails.meetingName),
                [typeHeader]: l10n.getStringForEnum("standard-order-type-filter-mode", row.orderKind, row.orderKind),
                [locationHeader]: toCsvText(getLocationText(row)),
                [priceHeader]: price ? moneyFactory.format(price) : "",
            }
        })

        download(csvOptions)(generateCsv(csvOptions)(exportRows))
    }, [rows, dateTimeFactory, moneyFactory, l10n])
}
